import { useEffect } from 'react';
import { KEYS } from 'platejs';
import { usePlateEditor, Plate, ParagraphPlugin, PlateLeaf } from 'platejs/react';
import { BoldPlugin, ItalicPlugin, StrikethroughPlugin, CodePlugin, HeadingPlugin } from '@platejs/basic-nodes/react';
import { LinkPlugin } from '@platejs/link/react';
import { ClassNames, css } from '@emotion/react';
import { fonts, lengths, zIndex } from 'decap-cms-ui-default';
import { fromJS } from 'immutable';
import { editorContainerStyles, EditorControlBar, editorStyleVars } from '../styles';
import { markdownToSlate, slateToMarkdown } from '../serializers';
import Editor from './components/Editor';
import Toolbar from './components/Toolbar';
import ParagraphElement from './components/Element/ParagraphElement';
import withProps from './withProps';
import CodeLeaf from './components/Leaf/CodeLeaf';
import HeadingElement from './components/Element/HeadingElement';
import ListElement from './components/Element/ListElement';
import BlockquoteElement from './components/Element/BlockquoteElement';
import LinkElement from './components/Element/LinkElement';
import ImageElement from './components/Element/ImageElement';
import ExtendedBlockquotePlugin from './plugins/ExtendedBlockquotePlugin';
import ImagePlugin from './plugins/ImagePlugin';
import ListPlugin from './plugins/ListPlugin';
import BreakPlugin from './plugins/BreakPlugin';
import ShortcodePlugin from './plugins/ShortcodePlugin';
import { TablePlugin, TableRowPlugin, TableCellPlugin } from './plugins/TablePlugin';
import defaultEmptyBlock from './defaultEmptyBlock';
import { mergeMediaConfig } from './mergeMediaConfig';
import filterInlineEditorComponents from '../filterInlineEditorComponents';
import { handleLinkClick } from './linkHandler';
import { handlePasteHtml } from './pasteHandler';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
function editorStyles({
  minimal
}) {
  return /*#__PURE__*/css("position:relative;font-family:", fonts.primary, ";min-height:", minimal ? 'auto' : lengths.richTextEditorMinHeight, ";margin-top:-", editorStyleVars.stickyDistanceBottom, ";padding:0;display:flex;flex-direction:column;z-index:", zIndex.zIndex100, ";white-space:pre-wrap;;label:editorStyles;" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"));
}
const emptyValue = [defaultEmptyBlock()];
export default function VisualEditor(props) {
  const {
    t,
    field,
    className,
    isDisabled,
    isEditorComponent,
    onMode,
    isShowModeToggle,
    onChange,
    getEditorComponents,
    getAsset
  } = props;
  let editorComponents = filterInlineEditorComponents(getEditorComponents());
  const codeBlockComponent = fromJS(editorComponents.find(({
    type
  }) => type === 'code-block'));
  editorComponents = codeBlockComponent || editorComponents.has('code-block') ? editorComponents : editorComponents.set('code-block', {
    label: 'Code Block',
    type: 'code-block'
  });
  mergeMediaConfig(editorComponents, field);
  function handleToggleMode() {
    onMode('raw');
  }
  function handleChange({
    value
  }) {
    const mdValue = slateToMarkdown(value, {
      voidCodeBlock: !!codeBlockComponent
    }, editorComponents);
    onChange(mdValue);
  }
  function handlePaste(event) {
    handlePasteHtml({
      event,
      editor,
      isDisabled
    });
  }
  const initialValue = props.value ? markdownToSlate(props.value, {
    editorComponents,
    voidCodeBlock: !!codeBlockComponent
  }) : emptyValue;
  const editor = usePlateEditor({
    override: {
      components: {
        [BoldPlugin.key]: withProps(PlateLeaf, {
          as: 'b'
        }),
        [CodePlugin.key]: CodeLeaf,
        [ItalicPlugin.key]: withProps(PlateLeaf, {
          as: 'em'
        }),
        [StrikethroughPlugin.key]: withProps(PlateLeaf, {
          as: 's'
        }),
        [ParagraphPlugin.key]: withProps(ParagraphElement, {
          as: 'p'
        }),
        [KEYS.h1]: withProps(HeadingElement, {
          variant: 'h1'
        }),
        [KEYS.h2]: withProps(HeadingElement, {
          variant: 'h2'
        }),
        [KEYS.h3]: withProps(HeadingElement, {
          variant: 'h3'
        }),
        [KEYS.h4]: withProps(HeadingElement, {
          variant: 'h4'
        }),
        [KEYS.h5]: withProps(HeadingElement, {
          variant: 'h5'
        }),
        [KEYS.h6]: withProps(HeadingElement, {
          variant: 'h6'
        }),
        ['ul']: withProps(ListElement, {
          variant: 'ul'
        }),
        ['ol']: withProps(ListElement, {
          variant: 'ol'
        }),
        ['li']: withProps(ListElement, {
          variant: 'li'
        }),
        ['blockquote']: BlockquoteElement,
        ['image']: withProps(ImageElement, {
          getAsset,
          field
        })
      }
    },
    plugins: [ParagraphPlugin, HeadingPlugin.configure({
      shortcuts: {
        h1: {
          keys: 'mod+1',
          handler: () => editor.tf.toggleBlock('h1')
        },
        h2: {
          keys: 'mod+2',
          handler: () => editor.tf.toggleBlock('h2')
        },
        h3: {
          keys: 'mod+3',
          handler: () => editor.tf.toggleBlock('h3')
        },
        h4: {
          keys: 'mod+4',
          handler: () => editor.tf.toggleBlock('h4')
        },
        h5: {
          keys: 'mod+5',
          handler: () => editor.tf.toggleBlock('h5')
        },
        h6: {
          keys: 'mod+6',
          handler: () => editor.tf.toggleBlock('h6')
        }
      }
    }), BoldPlugin, ItalicPlugin, StrikethroughPlugin.configure({
      shortcuts: {
        toggle: {
          keys: 'mod+shift+s'
        }
      }
    }), CodePlugin.configure({
      shortcuts: {
        toggle: {
          keys: 'mod+shift+c'
        }
      }
    }), ListPlugin, BreakPlugin, ImagePlugin, LinkPlugin.configure({
      node: {
        component: LinkElement
      },
      shortcuts: {
        toggleLink: {
          keys: 'mod+k',
          handler: () => {
            handleLinkClick({
              editor,
              t
            });
          }
        }
      }
    }), ExtendedBlockquotePlugin.configure({
      node: {
        component: BlockquoteElement
      }
    }), ShortcodePlugin, TablePlugin, TableRowPlugin, TableCellPlugin],
    value: initialValue
  });
  useEffect(() => {
    if (props.pendingFocus) {
      editor.tf.focus({
        edge: 'endEditor'
      });
      props.pendingFocus();
    }
  }, [props.pendingFocus]);
  return _jsx(ClassNames, {
    children: ({
      css,
      cx
    }) => _jsx("div", {
      className: cx(className, css`
              ${editorContainerStyles}
            `),
      children: _jsxs(Plate, {
        editor: editor,
        onChange: handleChange,
        children: [_jsx(EditorControlBar, {
          children: _jsx(Toolbar, {
            onToggleMode: handleToggleMode,
            buttons: field.get('buttons'),
            editorComponents: editorComponents,
            allowedEditorComponents: field.get('editor_components'),
            isShowModeToggle: isShowModeToggle,
            isEditorComponent: isEditorComponent,
            t: t,
            disabled: isDisabled
          })
        }), _jsx("div", {
          css: editorStyles({
            minimal: field.get('minimal')
          }),
          children: _jsx(Editor, {
            isDisabled: isDisabled,
            onPaste: handlePaste
          })
        })]
      })
    })
  });
}